import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.css'
import { Checkbox, Input, Select } from '../../../components/ModalInputs'
import Button from '../../../components/Button'
import { StudentsContext } from '../../../utils/context'
import classNames from 'classnames'
import { getStudentTable, deleteEducationRecord } from '../../../actions/students/other'
import { getLibrarySocialGroups, getLibraryMilRanks, getLibraryRooms } from '../../../actions/library'
import EducationModal from './extraModal/EducationModal'
import Confirmation from '../../../components/Confirmation/Confirmation'

const medalOptions = [
  { value: '', name: '' },
  { value: 'нет', name: 'нет' },
  { value: 'I степени', name: 'I степени' },
  { value: 'II степени', name: 'II степени' },
]

const tabs = ['Классы', 'Подгруппы', 'Доп. образование', 'Внеурочная деятельность']
const tabKeys = ['training_classes', 'training_subgroups', 'training_additional', 'training_outclass']

// Конфигурация колонок для каждого типа таблицы
const TABLE_COLUMNS = {
  training_classes: [
    { key: 'xp_date', label: 'Дата' },
    { key: 'status', label: 'Результат обучения' },
    { key: 'prikaz', label: '№ приказа' },
    { key: 'from_name', label: 'Класс откуда' },
    { key: 'to_name', label: 'Класс куда' },
    { key: 'protocol_number', label: 'Номер протокола' },
    { key: 'protocol_date', label: 'Дата протокола' }
  ],
  training_subgroups: [
    { key: 'xp_date', label: 'Дата' },
    { key: 'title', label: 'Предмет' },
    { key: 'g_from', label: 'Откуда' },
    { key: 'g_to', label: 'Куда' },
    { key: 'class_from', label: 'Класс откуда' },
    { key: 'class_to', label: 'Класс куда' }
  ],
  training_additional: [
    { key: 'xp_date', label: 'Дата' },
    { key: 'title', label: 'Предмет' },
    { key: 'g_from', label: 'Откуда' },
    { key: 'g_to', label: 'Куда' }
  ],
  training_outclass: [
    { key: 'xp_date', label: 'Дата' },
    { key: 'title', label: 'Предмет' },
    { key: 'g_from', label: 'Откуда' },
    { key: 'g_to', label: 'Куда' }
  ]
}

export default function Education() {
  const dispatch = useDispatch()
  const { allForms, addMode, studentTabData, updateInitialValues, studentId } = useContext(StudentsContext)
  const { socialGroups, milRanks, rooms } = useSelector(state => state.library)
  const [activeTab, setActiveTab] = useState(0)
  const [selectedRow, setSelectedRow] = useState(null)
  const [modalData, setModalData] = useState(null)
  const [modalAddMode, setModalAddMode] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { values, setFieldValue, setValues } = allForms

  const { training } = studentTabData || {}

  // Ref для отслеживания последних загруженных данных
  const lastSyncedRef = useRef(null)
  const loadedSubTabsRef = useRef(new Set())

  // Кастомный handleChange для вложенных полей
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFieldValue(`training.${name}`, type === 'checkbox' ? checked : value)
  }

  const renderOptions = ({ name, value, xp_key }) => (
    <option key={value || xp_key || name} value={value !== undefined ? value : (xp_key || name)}>
      {name}
    </option>
  )

  useEffect(() => {
    if (!socialGroups?.length) dispatch(getLibrarySocialGroups())
    if (!milRanks?.length) dispatch(getLibraryMilRanks())
    if (!rooms?.length) dispatch(getLibraryRooms())
  }, []) // eslint-disable-line

  // Синхронизация формы с данными из API (training)
  useEffect(() => {
    const dataJson = JSON.stringify(training)
    if (!addMode && training && lastSyncedRef.current !== dataJson) {
      lastSyncedRef.current = dataJson
      setValues(prev => ({
        ...prev,
        training: { ...training }
      }))
      if (updateInitialValues) {
        updateInitialValues('training', training)
      }
    }
  }, [training, addMode, setValues, updateInitialValues])

  // Загрузка данных вложенного таба при переключении
  useEffect(() => {
    if (!addMode && studentId) {
      const tabKey = tabKeys[activeTab]
      if (!loadedSubTabsRef.current.has(tabKey)) {
        loadedSubTabsRef.current.add(tabKey)
        dispatch(getStudentTable(tabKey, studentId))
      }
    }
    // Сбрасываем выделение при смене таба
    setSelectedRow(null)
  }, [activeTab, addMode, studentId, dispatch])

  // Получаем данные текущей вложенной таблицы
  const getCurrentTabData = () => {
    const tabKey = tabKeys[activeTab]
    const data = studentTabData?.[tabKey]
    return Array.isArray(data) ? data : []
  }

  const currentTabKey = tabKeys[activeTab]
  const tableData = getCurrentTabData()
  const columns = TABLE_COLUMNS[currentTabKey]

  // Обработчики кнопок
  const handleAdd = () => {
    setModalAddMode(true)
    setModalData({})
  }

  const handleEdit = () => {
    if (selectedRow !== null && tableData[selectedRow]) {
      setModalAddMode(false)
      setModalData(tableData[selectedRow])
    }
  }

  const handleDelete = () => {
    if (selectedRow !== null && tableData[selectedRow]) {
      setShowDeleteConfirm(true)
    }
  }

  const confirmDelete = async () => {
    const record = tableData[selectedRow]
    const recordId = record.xp_key
    await dispatch(deleteEducationRecord(studentId, currentTabKey, recordId))
    setSelectedRow(null)
    setShowDeleteConfirm(false)
  }

  const closeModal = () => {
    setModalData(null)
    setSelectedRow(null)
  }

  return (
    <div className={styles.tabWrapper}>
      {/* Модальное окно для создания/редактирования */}
      {modalData && (
        <EducationModal
          data={modalData}
          closeModal={closeModal}
          tabType={currentTabKey}
          studentId={studentId}
          addMode={modalAddMode}
        />
      )}

      {/* Подтверждение удаления */}
      {showDeleteConfirm && (
        <Confirmation
          title="Вы уверены, что хотите удалить запись?"
          acceptConfirmation={confirmDelete}
          rejectConfirmation={() => setShowDeleteConfirm(false)}
        />
      )}

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>До обучения в учебном заведении</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Дет. Сад (кол. лет)'
            name='kindergarten'
            value={(values.training || {}).kindergarten || ''}
            onChange={handleChange}
            style={{ width: '120px' }}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Учебное заведение до поступления'
            highlight
            name='schoolfrom'
            value={(values.training || {}).schoolfrom || ''}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Дата выбытия'
            type='date'
            name='dateleaveschool'
            value={(values.training || {}).dateleaveschool || ''}
            onChange={handleChange}
            style={{ width: '150px' }}
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Зачисление в учебное заведение</h3>
        <div className={styles.inputsRow}>
          <Select
            label='Социальная группа'
            name='socialgroup'
            value={(values.training || {}).socialgroup || ''}
            onChange={handleChange}
            style={{ width: '200px' }}
          >
            <option value=''>—</option>
            {(socialGroups || []).map(g => (
              <option key={g.xp_key || g.id} value={g.xp_key || g.id}>{g.name}</option>
            ))}
          </Select>
          <Checkbox
            label='Группа повышенного психолого-педагогического внимания'
            name='psychologicalattention'
            checked={!!(values.training || {}).psychologicalattention}
            onChange={handleChange}
          />
        </div>
        <div className={styles.inputsRow}>
          <Select
            label='Наличие специального звания'
            name='mil_rank'
            value={(values.training || {}).mil_rank || ''}
            onChange={handleChange}
            style={{ width: '100%' }}
          >
            <option value=''>—</option>
            {(milRanks || []).map(r => (
              <option key={r.name || r} value={r.name || r}>{r.name || r}</option>
            ))}
          </Select>
        </div>
        <div className={styles.inputsRow}>
          <Select
            label='Комната для проживания'
            name='rid'
            value={(values.training || {}).rid || ''}
            onChange={handleChange}
            style={{ width: '250px' }}
          >
            <option value=''>—</option>
            {(rooms || []).map(r => (
              <option key={r.rid || r.id} value={r.rid || r.id}>{r.name || r.room_name}</option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Обучение</h3>
        <div className={styles.inputsRow}>
          <span className={styles.labelText}>Награжден медалью "За особые успехи в учении"</span>
          <Select
            name='nagrajden'
            value={(values.training || {}).nagrajden || ''}
            onChange={handleChange}
            style={{ width: '120px' }}
          >
            {medalOptions.map(renderOptions)}
          </Select>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabsHeader}>
          {tabs.map((tab, index) => (
            <div
              key={index}
              className={classNames(styles.tab, { [styles.activeTab]: activeTab === index })}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </div>
          ))}
        </div>
        <table className={styles.admissionTable}>
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((row, idx) => (
                <tr
                  key={row.xp_key || idx}
                  onClick={() => setSelectedRow(idx)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedRow === idx ? '#DEECFF' : 'transparent'
                  }}
                >
                  {columns.map(col => (
                    <td key={col.key}>{row[col.key] || '-'}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ height: '100px', textAlign: 'center', color: '#666' }}>
                  {addMode ? 'Сначала сохраните основные данные' : 'Нет данных'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.inputsRow} style={{ marginTop: '0.5rem' }}>
        <Button mode='bordered' onClick={handleAdd} disabled={addMode}>Добавить</Button>
        <Button mode='bordered' onClick={handleEdit} disabled={addMode || selectedRow === null}>Изменить</Button>
        <Button mode='bordered' onClick={handleDelete} disabled={addMode || selectedRow === null}>Удалить</Button>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Выбытие из учебного заведения</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Дата выбытия из учебного заведения'
            type='date'
            name='dateleavepansion'
            value={(values.training || {}).dateleavepansion || ''}
            onChange={handleChange}
            style={{ width: '200px' }}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='№ протокола Педсовета об отчислении'
            name='numberprotocolleavepansion'
            value={(values.training || {}).numberprotocolleavepansion || ''}
            onChange={handleChange}
            style={{ width: '250px' }}
          />
          <Input
            label='Причины отчисления'
            name='reasonleave'
            value={(values.training || {}).reasonleave || ''}
            onChange={handleChange}
            style={{ width: '250px' }}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='№ приказа по учебному заведению'
            name='numberorderleavepansion'
            value={(values.training || {}).numberorderleavepansion || ''}
            onChange={handleChange}
            style={{ width: '250px' }}
          />
          <Input
            label='Примечание к отчислению'
            name='commentleave'
            value={(values.training || {}).commentleave || ''}
            onChange={handleChange}
            style={{ width: '250px' }}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Место выбытия'
            name='placeleave'
            value={(values.training || {}).placeleave || ''}
            onChange={handleChange}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className={styles.checkboxGroup}>
        <span className={styles.groupLabel}>Для отчисленных</span>
        <Checkbox
          label='Переход в гражд. школу'
          name='civilschool'
          checked={!!(values.training || {}).civilschool}
          onChange={handleChange}
        />
        <Checkbox
          label='Переход в прочие ОУ'
          name='enterotherschool'
          checked={!!(values.training || {}).enterotherschool}
          onChange={handleChange}
        />
      </div>

      <div className={styles.checkboxGroup}>
        <span className={styles.groupLabel}>Для выпускников</span>
        <Checkbox
          label='В гражд. ВУЗ'
          name='entercivilhighschool'
          checked={!!(values.training || {}).entercivilhighschool}
          onChange={handleChange}
        />
        <Checkbox
          label='В ВУЗ МО РФ'
          name='enterwarhighschool'
          checked={!!(values.training || {}).enterwarhighschool}
          onChange={handleChange}
        />
        <Checkbox
          label='В ВУЗ др. силовых структур'
          name='enterwarhighschoolother'
          checked={!!(values.training || {}).enterwarhighschoolother}
          onChange={handleChange}
        />
        <Checkbox
          label='В ВУЦ'
          name='enterwuts'
          checked={!!(values.training || {}).enterwuts}
          onChange={handleChange}
        />
        <Checkbox
          label='В СПО'
          name='enterspo'
          checked={!!(values.training || {}).enterspo}
          onChange={handleChange}
        />
        <Checkbox
          label='Не поступил(а)'
          name='notenter'
          checked={!!(values.training || {}).notenter}
          onChange={handleChange}
        />
      </div>

      <div className={styles.inputsRow} style={{ marginTop: '1rem' }}>
        <Button mode='bordered'>Зачетная книжка</Button>
        <Button mode='bordered'>Библиотека</Button>
      </div>
    </div>
  )
}
